"use client"

import "./testimonials.css"
import "../../features/footer/footer.css";
import Fbimg from "../../features/footer/fb2.png";
import Twimg from "../../features/footer/twitter.png";
import Gimg from "../../features/footer/G+.png";
import Linkimg from "../../features/footer/link1.png";
import Instaimg from "../../features/footer/insta.png";
import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { fetchTestimonialsPublicAsync, selectTestimonials } from "../AdminPanel/Testimonials/TestimonialsSlice"
import { Box, SkeletonCircle, SkeletonText } from "@chakra-ui/react"
import { NoDataFound } from "../../pages/CommonPublicPages/Basic/NoDataFound"

export function Testimonials() {
  const [currentIndexLeft, setCurrentIndexLeft] = useState(0)
  const [currentIndexRight, setCurrentIndexRight] = useState(0)
  const [isLoading, setIsLoading] = useState(true)

  const dispatch = useDispatch()
  const testimonials = useSelector(selectTestimonials)
  const [leftTestimonials, setLeftTestimonials] = useState([])
  const [rightTestimonials, setRightTestimonials] = useState([])

  useEffect(() => {
    setIsLoading(true)
    dispatch(fetchTestimonialsPublicAsync())
      .then(() => {
        setIsLoading(false)
      })
      .catch(() => {
        setIsLoading(false)
      })
  }, [dispatch])

  useEffect(() => {
    if (testimonials?.length > 0) {
      const middleIndex = Math.ceil(testimonials.length / 2)
      setLeftTestimonials(testimonials.slice(0, middleIndex))
      setRightTestimonials(testimonials.slice(middleIndex))
    }
  }, [testimonials])

  useEffect(() => {
    const intervalLeft = setInterval(() => {
      setCurrentIndexLeft((prevIndex) => (prevIndex === leftTestimonials?.length - 1 ? 0 : prevIndex + 1))
    }, 5000)

    const intervalRight = setInterval(() => {
      setCurrentIndexRight((prevIndex) => (prevIndex === rightTestimonials?.length - 1 ? 0 : prevIndex + 1))
    }, 5000)

    return () => {
      clearInterval(intervalLeft)
      clearInterval(intervalRight)
    }
  }, [leftTestimonials?.length, rightTestimonials?.length])

  const renderTestimonial = (testimonial, index, currentIndex, direction) => (
    <div
      key={testimonial?.uuid}
      className={`absolute w-full h-full transition-all duration-700 ease-in-out p-2 ${currentIndex === index
        ? "opacity-100 translate-y-0"
        : direction === "top"
          ? "opacity-0 -translate-y-full"
          : "opacity-0 translate-y-full"
        }`}
    >
      <div className="bg-white h-full rounded-3xl transition duration-300 transform hover:-translate-y-2 overflow-hidden relative">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-400/10 to-blue-600/10 z-0"></div>
        <div className="relative z-10 p-6 flex flex-col h-full">
          <div className="flex items-center">
            <div className="ml-4">
              <h3 className="text-xl text-start font-bold text-blue-800">{testimonial.name}</h3>
              <p className="text-blue-600 font-medium">
                {testimonial.company_name}, {testimonial.designation}
              </p>
            </div>
          </div>
          <div className="bg-blue-100 rounded-xl p-3 flex-grow overflow-auto testimonial-scroll">
            <p className="text-gray-700 leading-relaxed text-lg italic">{testimonial.review_description}</p>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <div className="flex">
              {[...Array(5)].map((_, i) => {
                const isFilled = i < testimonial.review_rating
                return (
                  <svg
                    key={i}
                    className={`w-5 h-5 ${isFilled ? "text-yellow-400" : "text-gray-300"} fill-current`}
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                )
              })}
            </div>
            <span className="text-blue-600 font-semibold text-sm">Verified Review</span>
          </div>
        </div>
      </div>
    </div>
  )

  // Skeleton loader for testimonial cards
  const renderSkeletonTestimonial = () => (
    <div className="w-full max-w-xs sm:max-w-sm h-[26rem] p-2">
      <Box className="bg-white h-full rounded-3xl p-6" boxShadow="md">
        <div className="flex items-center mb-4">
          <SkeletonCircle size="10" />
          <div className="ml-4">
            <SkeletonText noOfLines={2} spacing="2" skeletonHeight="3" width="150px" />
          </div>
        </div>
        <Box className="bg-blue-50 rounded-xl p-3 flex-grow">
          <SkeletonText mt="2" noOfLines={5} spacing="4" skeletonHeight="3" />
        </Box>
        <Box mt="4">
          <SkeletonText noOfLines={1} width="120px" skeletonHeight="3" />
        </Box>
      </Box>
    </div>
  )

  // Function to render testimonial content based on loading and data state
  const renderTestimonialContent = () => {
    if (isLoading) {
      // Show skeleton loader when data is being fetched
      return (
        <div className="w-full lg:w-2/3 flex flex-col sm:flex-row justify-evenly items-center gap-6 perspective-1000">
          {renderSkeletonTestimonial()}
          {renderSkeletonTestimonial()}
        </div>
      )
    } else if (!Array.isArray(testimonials) || testimonials.length === 0) {
      // Show NoDataFound component when there's no data
      return (
        <div className="w-full lg:w-2/3 flex justify-center items-center">
          <NoDataFound
            title="No Testimonials Found"
            message="We couldn't find any testimonials at the moment. Please check back later."
          />
        </div>
      )
    } else {
      // Show actual testimonial data
      return (
        <div className="w-full lg:w-2/3 flex flex-col sm:flex-row justify-evenly items-center gap-6 perspective-1000 flex-wrap">
          <div className="relative w-full max-w-xs sm:max-w-sm min-h-[26rem] h-auto flex overflow-hidden">
            {leftTestimonials.map((testimonial, index) =>
              renderTestimonial(testimonial, index, currentIndexLeft, "top"),
            )}
          </div>

          <div className="relative w-full max-w-xs sm:max-w-sm min-h-[26rem] h-auto flex overflow-hidden">
            {rightTestimonials.map((testimonial, index) =>
              renderTestimonial(testimonial, index, currentIndexRight, "bottom"),
            )}
          </div>
        </div>
      )
    }
  }

  return (
    <div className="testi">


      <div className="py-20">
        <div className="bg-gradient-to-br from-blue-600 to-blue-300 w-full py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
              <div className="w-full lg:w-1/3 text-center lg:text-left">
                <h2 className="text-4xl sm:text-5xl font-extrabold text-white leading-tight">
                  Meet Our{" "}
                  <span className="text-yellow-300 underline decoration-wavy decoration-yellow-300/30 underline-offset-8">
                    {/* Satisfied */}
                    Happily Hired
                  </span>{" "}
                  Job Seekers
                </h2>
                <p className="mt-6 text-xl text-blue-100 leading-relaxed">
                  Discover success stories from professionals who found their dream roles with us. Join our community
                  today!
                </p>
                <button className="mt-8 px-8 py-4 bg-white text-blue-600 font-bold rounded-full shadow-lg hover:bg-yellow-300 hover:text-blue-700 transition duration-300 ease-in-out transform hover:-translate-y-1 hover:scale-105 focus:outline-none focus:ring-4 focus:ring-yellow-300 focus:ring-opacity-50">
                  Join Our Community
                </button>
                <div className="flex items-center justify-center">

                  <div className="footer-socials flex justify-center md:justify-start gap-3 mt-4">
                    <a href="https://www.facebook.com" className="social-link" aria-label="Facebook" target="_blank" rel="noopener noreferrer">
                      <img src={Fbimg || "/placeholder.svg"} alt="Facebook" />
                    </a>
                    <a href="https://www.twitter.com" className="social-link" aria-label="Twitter" target="_blank" rel="noopener noreferrer">
                      <img src={Twimg || "/placeholder.svg"} alt="Twitter" />
                    </a>
                    <a href="https://plus.google.com" className="social-link" aria-label="Google Plus" target="_blank" rel="noopener noreferrer">
                      <img src={Gimg || "/placeholder.svg"} alt="Google+" />
                    </a>
                    <a href="https://www.linkedin.com" className="social-link" aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">
                      <img src={Linkimg || "/placeholder.svg"} alt="LinkedIn" />
                    </a>
                    <a href="https://www.instagram.com" className="social-link" aria-label="Instagram" target="_blank" rel="noopener noreferrer">
                      <img src={Instaimg || "/placeholder.svg"} alt="Instagram" />
                    </a>
                  </div>
                </div>
              </div>

              {renderTestimonialContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
